import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../api/client";
import { useDomainStore } from "../stores/domainStore";
import { Card, CardContent } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/Table";
import { Skeleton } from "../components/ui/Skeleton";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";

const statusVariant = {
  pending: "warning",
  processing: "secondary",
  completed: "success",
  failed: "destructive",
};

export default function DomainDocumentsPage() {
  const { id } = useParams();
  const { domains } = useDomainStore();
  const { addToast } = useToast();
  const domain = domains.find((d) => d.id === id);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    loadDocs();
  }, [id]);

  // Poll document statuses every 3s when there are pending/processing docs
  useEffect(() => {
    const hasActive = docs.some((d) => d.status === "pending" || d.status === "processing");
    if (!hasActive) return;

    const interval = setInterval(() => {
      loadDocs();
    }, 3000);

    return () => clearInterval(interval);
  }, [docs]);

  async function loadDocs() {
    try {
      const { data } = await client.get("/api/documents", {
        params: { domain_id: id },
      });
      setDocs(data.documents || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter((f) => {
        const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
        return allowed.includes(f.type);
      });

      if (files.length > 0) {
        handleBatchUpload(files);
      } else {
        addToast("Only PDF and image files are allowed", "error");
      }
    }
  }, [id]);

  async function handleBatchUpload(files) {
    if (isUploading) return;

    const queueItems = files.map((file) => ({
      id: Math.random().toString(36).slice(2),
      name: file.name,
      size: file.size,
      status: "queued",
      progress: 0,
    }));

    setUploadQueue((prev) => [...prev, ...queueItems]);
    setIsUploading(true);

    const form = new FormData();
    files.forEach((file) => form.append("files", file));
    form.append("domain_id", id);

    try {
      // Mark all as uploading
      setUploadQueue((prev) =>
        prev.map((item) =>
          queueItems.find((q) => q.id === item.id)
            ? { ...item, status: "uploading" }
            : item
        )
      );

      const { data } = await client.post("/api/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadQueue((prev) =>
            prev.map((item) =>
              queueItems.find((q) => q.id === item.id)
                ? { ...item, progress: percent, status: percent < 100 ? "uploading" : "processing" }
                : item
            )
          );
        },
      });

      // Mark completed, remove after delay
      setUploadQueue((prev) =>
        prev.map((item) =>
          queueItems.find((q) => q.id === item.id)
            ? { ...item, status: "done", progress: 100 }
            : item
        )
      );

      addToast(`${data.count} document(s) queued for processing`, "success");
      loadDocs();

      setTimeout(() => {
        setUploadQueue((prev) => prev.filter((item) => !queueItems.find((q) => q.id === item.id)));
      }, 3000);
    } catch (err) {
      addToast(err.response?.data?.error || "Upload failed", "error");
      setUploadQueue((prev) =>
        prev.map((item) =>
          queueItems.find((q) => q.id === item.id)
            ? { ...item, status: "failed" }
            : item
        )
      );
    } finally {
      setIsUploading(false);
    }
  }

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }

  async function handleDelete(docId) {
    if (!window.confirm("Delete this document? This removes the file, chunks, embeddings, and graph data.")) return;

    try {
      await client.delete(`/api/documents/${docId}`);
      addToast("Document deleted", "success");
      loadDocs();
    } catch (err) {
      addToast(err.response?.data?.error || "Delete failed", "error");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{domain?.name || "Domain"} Documents</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{docs.length} documents</p>
        </div>
        <Link to={`/domains/${id}/chat`}>
          <Button variant="outline">Chat with Documents</Button>
        </Link>
      </div>

      {/* Batch Upload Dropzone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
          dragActive
            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20"
            : "border-slate-300 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-600"
        }`}
      >
        <input
          type="file"
          id="batch-upload"
          multiple
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length > 0) {
              handleBatchUpload(Array.from(e.target.files));
            }
          }}
        />
        <label htmlFor="batch-upload" className="cursor-pointer">
          <div className="space-y-3">
            <div className="mx-auto h-12 w-12 text-slate-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
              {isUploading ? "Uploading..." : "Drop files here or click to upload"}
            </p>
            <p className="text-xs text-slate-500">PDF, JPG, PNG, WebP up to 50MB each · Up to 50 files at once</p>
          </div>
        </label>
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="py-3">
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Upload Queue ({uploadQueue.filter((q) => q.status !== "done").length} active)
              </div>
              {uploadQueue.map((item) => (
                <div key={item.id} className="flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="truncate font-medium">{item.name}</span>
                      <span className="text-xs text-slate-500 shrink-0 ml-2">{formatBytes(item.size)}</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          item.status === "failed"
                            ? "bg-red-500"
                            : item.status === "done"
                            ? "bg-emerald-500"
                            : "bg-emerald-500"
                        }`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </div>
                  {item.status === "uploading" && <Spinner size="sm" />}
                  {item.status === "processing" && <Badge variant="warning">Queued</Badge>}
                  {item.status === "done" && <Badge variant="success">Done</Badge>}
                  {item.status === "failed" && <Badge variant="destructive">Failed</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Table */}
      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {!loading && docs.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 dark:text-slate-400">No documents in this domain yet. Drop files above to upload.</p>
          </CardContent>
        </Card>
      )}

      {!loading && docs.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <span className="font-medium">{doc.title}</span>
                        {doc.summary && (
                          <p className="text-xs text-slate-500 line-clamp-2 max-w-md">{doc.summary}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {doc.Tags?.length > 0 ? (
                          doc.Tags.map((tag) => (
                            <Badge key={tag.id} variant="secondary" className="text-xs">
                              {tag.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{doc.page_count || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[doc.status] || "default"}>{doc.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link to={`/domains/${id}/documents/${doc.id}`}>
                          <Button size="sm" variant="ghost">View</Button>
                        </Link>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete document"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
